import { useCallback, useEffect, useMemo, useState } from "react";

import { AlertCircle, MessageCircle, RefreshCw, Target } from "lucide-react";

import { Button } from "@/components/ui/Button";

import { formatDate } from "@/utils/format";

import {

  defaultMarketingPeriod,

  fetchMarketingDashboard,

  type InstagramIndicators,

  type InstagramKpis,

  type MarketingDashboard,

  type MarketingPeriod,

} from "@/services/marketing";

import { DateFilterBar } from "./DateFilterBar";

import { MarketingMetricsTabs } from "./MarketingMetricsTabs";

import { MarketingProductionSection } from "./MarketingProductionSection";

import { MarketingGoalsModal } from "./MarketingGoalsModal";

import { MarketingGoalsTracking } from "./MarketingGoalsTracking";

import { MarketingReportModal } from "./MarketingReportModal";

import { MarketingAccountFilter } from "./MarketingAccountFilter";

import { MarketingProductionGalleryModal } from "./MarketingProductionGalleryModal";

import type { ProductionGalleryKind } from "@/services/marketing";

import { InstagramEngagementRateByDayChart } from "./charts/InstagramEngagementRateByDay";

import { InstagramReachByDayChart } from "./charts/InstagramReachByDay";

import { InstagramFollowersByDayChart } from "./charts/InstagramFollowersByDay";

import { InstagramNewFollowersByDayChart } from "./charts/InstagramNewFollowersByDay";

import { InstagramTopPostsPanel } from "./charts/InstagramTopPosts";

import { useAppAccess } from "@/hooks/useAppAccess";



function computeIndicators(kpis: InstagramKpis): InstagramIndicators {

  const interactions =

    kpis.likes + kpis.comments + kpis.shares + kpis.saves + (kpis.replies ?? 0);

  const reach = kpis.reach;

  if (reach <= 0) {

    return {

      interactions,

      engagementRate: 0,

      viewsPerReach: 0,

      likesPer1kReach: 0,

      saveRate: 0,

    };

  }

  return {

    interactions,

    engagementRate: (interactions / reach) * 100,

    viewsPerReach: kpis.views / reach,

    likesPer1kReach: (kpis.likes / reach) * 1000,

    saveRate: (kpis.saves / reach) * 100,

  };

}



export function MarketingPage() {

  const [filterValues, setFilterValues] = useState<MarketingPeriod>(() => defaultMarketingPeriod());

  const [applied, setApplied] = useState<MarketingPeriod>(() => defaultMarketingPeriod());

  const [data, setData] = useState<MarketingDashboard | null>(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [goalsModalOpen, setGoalsModalOpen] = useState(false);

  const [section, setSection] = useState<"painel" | "metas">("painel");

  const [goalsMonth, setGoalsMonth] = useState("");

  const [goalsRevision, setGoalsRevision] = useState(0);

  const [reportModalOpen, setReportModalOpen] = useState(false);

  const [galleryKind, setGalleryKind] = useState<ProductionGalleryKind | null>(null);

  const [selectedAccount, setSelectedAccount] = useState("all");

  const { isAdmin, isMaster } = useAppAccess();

  const canManageReport = isAdmin || isMaster;



  const loadDashboard = useCallback(async (period: MarketingPeriod, account = "all", refresh = false) => {

    setLoading(true);

    setError(null);

    try {

      const dashboard = await fetchMarketingDashboard(period, account, { refresh });

      setData(dashboard);

      setApplied(period);

      setSelectedAccount(dashboard.selectedAccount);

    } catch (err) {

      setError(err instanceof Error ? err.message : "Erro ao carregar métricas.");

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    void loadDashboard(defaultMarketingPeriod());

  }, [loadDashboard]);



  const handleApply = () => {

    if (!filterValues.startDate || !filterValues.endDate) return;

    void loadDashboard(filterValues, selectedAccount, true);

  };



  const handleReset = () => {

    const period = defaultMarketingPeriod();

    setFilterValues(period);

    void loadDashboard(period, selectedAccount, true);

  };



  const handleAccountChange = (accountKey: string) => {

    setSelectedAccount(accountKey);

    void loadDashboard(applied, accountKey, true);

  };



  const openGoals = (month?: string) => {

    setGoalsMonth(month || applied.endDate.slice(0, 7));

    setGoalsModalOpen(true);

  };



  const galleryItems =

    galleryKind && data?.productionItems ? data.productionItems[galleryKind] ?? [] : [];



  const instagram = data?.instagram;

  const ig = instagram?.kpis;

  const indicators = ig

    ? (instagram?.indicators ?? computeIndicators(ig))

    : undefined;

  const isFirstLoad = loading && data == null;



  const pageTitle = useMemo(() => {
    if (!instagram?.username) return "Instagram";
    if (selectedAccount === "all" && (data?.accounts.length ?? 0) > 1) {
      return "Instagram — Todas as contas";
    }
    return `Instagram — @${instagram.username}`;
  }, [instagram?.username, selectedAccount, data?.accounts.length]);



  return (

    <div className="flex flex-col gap-6">

      <div className="flex flex-wrap items-center justify-between gap-3">

        <div>

          <h1 className="text-xl font-bold text-slate-800">{pageTitle}</h1>

          <p className="mt-0.5 text-sm text-slate-500">

            Métricas orgânicas do Instagram

            {data ? (

              <> — {formatDate(applied.startDate)} a {formatDate(applied.endDate)}</>

            ) : null}

          </p>

        </div>

        <Button

          variant="ghost"

          size="sm"

          onClick={handleApply}

          loading={loading}

          leftIcon={<RefreshCw className="h-4 w-4" />}

        >

          Atualizar

        </Button>

      </div>

      <div className="flex w-fit gap-1 rounded-lg bg-slate-100 p-1">
        <button
          type="button"
          onClick={() => setSection("painel")}
          className={`rounded-md px-4 py-2 text-sm font-semibold ${section === "painel" ? "bg-white text-brand-700 shadow-sm" : "text-slate-500"}`}
        >
          Painel
        </button>
        <button
          type="button"
          onClick={() => setSection("metas")}
          className={`rounded-md px-4 py-2 text-sm font-semibold ${section === "metas" ? "bg-white text-brand-700 shadow-sm" : "text-slate-500"}`}
        >
          Metas
        </button>
      </div>

      {section === "metas" ? (
        <MarketingGoalsTracking
          revision={goalsRevision}
          onDefineGoals={(month) => openGoals(month)}
        />
      ) : (
      <>



      <DateFilterBar

        values={filterValues}

        onChange={setFilterValues}

        onApply={handleApply}

        onReset={handleReset}

        loading={loading}

        extraActions={
          <>
            {canManageReport ? (
              <Button
                variant="secondary"
                size="md"
                onClick={() => setReportModalOpen(true)}
                leftIcon={<MessageCircle className="h-4 w-4" />}
              >
                Disparo WhatsApp
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="md"
              onClick={() => openGoals()}
              leftIcon={<Target className="h-4 w-4" />}
            >
              Definir metas
            </Button>
          </>
        }

      />



      <MarketingAccountFilter

        accounts={data?.accounts ?? []}

        value={selectedAccount}

        onChange={handleAccountChange}

        disabled={loading}

      />



      {error && (

        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">

          <AlertCircle className="h-4 w-4 flex-shrink-0" />

          <span>{error}</span>

          <button onClick={handleApply} className="ml-auto text-xs text-red-500 underline hover:text-red-700">

            Tentar novamente

          </button>

        </div>

      )}



      {data?.warnings && data.warnings.length > 0 && (

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">

          <p className="mb-1 font-semibold">Avisos</p>

          <ul className="list-inside list-disc space-y-0.5 text-xs sm:text-sm">

            {data.warnings.map((w) => (

              <li key={w}>{w}</li>

            ))}

          </ul>

        </div>

      )}



      <MarketingMetricsTabs

        kpis={ig}

        followers={instagram?.followers}

        indicators={indicators}

        published={data?.publishedPerformance}

        loading={isFirstLoad}

        username={instagram?.username}

        accounts={data?.accounts}

        selectedAccount={selectedAccount}

      />



      <MarketingProductionSection

        production={data?.production}

        goals={data?.goals ?? { feed: 0, reels: 0, stories: 0 }}

        loading={isFirstLoad}

        onOpenGallery={setGalleryKind}

      />



      <MarketingReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
      />



      <MarketingProductionGalleryModal

        open={galleryKind !== null}

        kind={galleryKind}

        items={galleryItems}

        onClose={() => setGalleryKind(null)}

      />



      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">

        <InstagramFollowersByDayChart data={data?.followersByDay ?? []} loading={loading} />

        <InstagramNewFollowersByDayChart data={data?.newFollowersByDay ?? []} loading={loading} />

      </div>



      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">

        <InstagramEngagementRateByDayChart data={data?.metricsByDay ?? []} loading={loading} />

        <InstagramReachByDayChart data={data?.metricsByDay ?? []} loading={loading} />

      </div>



      <InstagramTopPostsPanel posts={data?.topPosts ?? []} loading={loading} />

      </>
      )}

      <MarketingGoalsModal
        open={goalsModalOpen}
        initialMonth={goalsMonth || applied.endDate.slice(0, 7)}
        onClose={() => setGoalsModalOpen(false)}
        onSaved={() => {
          setGoalsRevision((current) => current + 1);
          void loadDashboard(applied, selectedAccount);
        }}
      />

    </div>

  );

}


